import {
  useDeleteComment,
  useEditComment,
  useCreateShiftComment,
  useFetchShiftComments,
  TimeRange,
  useRequiredConfig
} from '@domain/shifthandover'
import {ContentBox, ContentMode} from '@hconnect/common/components/ContentBox'
import {usePermission} from '@hproduce/permissions'
import {LinearProgress, Theme, Box} from '@mui/material'
import {SystemStyleObject} from '@mui/system/styleFunctionSx/styleFunctionSx'
import {CommentsList, NewComment} from '@widgets/shift-event-details'
import moment from 'moment'
import React from 'react'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../consts'

type CommentsProps = {
  plantId: string
  timeRange: TimeRange
  contentMode: ContentMode
  isSingleShift: boolean
  timezone: string
  bodySx?: SystemStyleObject<Theme>
  headerSx?: SystemStyleObject<Theme>
}

export const ShiftComments: React.FC<CommentsProps> = ({
  plantId,
  headerSx,
  bodySx,
  timeRange,
  contentMode,
  isSingleShift,
  timezone
}) => {
  const {country} = useRequiredConfig(plantId)
  const plantContext = {plantId, countryId: country}
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const {endDate} = timeRange
  const now = moment().tz(timezone)
  const {
    data: comments,
    isLoading: commentsLoading,
    isFetching: commentsFetching
  } = useFetchShiftComments({plantId, from: timeRange.startDate, to: timeRange.endDate, timezone})

  const canAddComments = usePermission({
    plantContext,
    permissionTypes: ['ADD_PLANT_EVENT_COMMENTS']
  })
  const canViewComments = usePermission({
    plantContext,
    permissionTypes: ['VIEW_PLANT_EVENT_COMMENTS']
  })

  // create comment either for current time or clamp it to shift end time to ensure it stays within the shift
  const submittedForTimestamp = now.isAfter(endDate) ? endDate : now.toISOString()

  const createComment = useCreateShiftComment({plantId, timezone})
  const deleteCommentAction = useDeleteComment({plantId, timezone, isShiftComment: true})
  const editCommentAction = useEditComment({plantId, timezone, isShiftComment: true})

  const showLinearProgress = commentsLoading || commentsFetching

  return (
    <ContentBox
      title={`${t('shiftEvent.label.comments')}${comments ? ` (${comments.length})` : ''}`}
      titleTestId="shift-events-heading"
      bodyWithPadding
      headerSx={headerSx}
      bodySx={bodySx}
      mode={contentMode}
      data-test-id="shift-comments-container"
    >
      <Box data-test-id="shift-comments" pt={1}>
        {showLinearProgress && <LinearProgress data-test-id="events-comments-loading" />}
        {comments && (
          <div>
            {canAddComments && isSingleShift && (
              <NewComment
                submittedForTimestamp={submittedForTimestamp}
                createCommentAction={createComment}
              />
            )}
            {canViewComments && (
              <CommentsList
                comments={comments}
                editCommentAction={editCommentAction}
                deleteCommentAction={deleteCommentAction}
                plantId={plantId}
                country={country}
              />
            )}
          </div>
        )}
      </Box>
    </ContentBox>
  )
}
